import { nextTick } from "vue";
import { EntityResolver } from "../../Services/EntityResolver";
import { EntityNavigator } from "../../Services/EntityNavigator";
import { EntityErrorHandler } from "../../Services/EntityErrorHandler";
import { useUserStore } from "../../Stores/User";
import { RouteHelper } from "../../Helpers/RouteHelper";
import { ROUTE_NAMES } from "../../Constants/routes";
import { useAppState } from "../../Stores/AppState";
import { useWorkspace } from "../../Stores/Workspaces";
import { logEntityLocatorNav } from "../../Helpers/EntityLocatorNavDebug";
import {
    shouldSkipWorkEntityNavigation,
    waitForRestoredWorkspaceFilter,
} from "../../Helpers/workEntityNavigation";

async function waitForWorkPanelPaint(cameFromNonWorkModule) {
    await nextTick();

    if (!cameFromNonWorkModule) {
        return;
    }

    await new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
    });
    await nextTick();
}

export async function handleWorkEntityNavigation(to, from, next) {
    try {
        // Check if this is a URL-only update (entity already active).
        // Check BEFORE any async work so we catch the flag reliably.
        // Locator navigations clear skipEntityNavigation immediately before
        // router.push (after any await). Do not let a lingering
        // entity_navigate_with_locator (held ~550ms after already-on-URL
        // locate) defeat a later updateUrlForEntity skip.
        const shouldSkip = shouldSkipWorkEntityNavigation({
            skipEntityNavigation: RouteHelper.skipEntityNavigation,
        });

        if (shouldSkip) {
            // Reset flag and skip EntityNavigator to prevent LocationAnimation
            // Entity is already active, we're just syncing URL
            logEntityLocatorNav("workEntityGuard.skip", {
                reason: "skipEntityNavigation_flag",
                to: to.fullPath,
                from: from.fullPath,
            });
            RouteHelper.skipEntityNavigation = false;
            next();
            return;
        }

        // Skip if navigating from same work entity route with same identifier
        if (
            from.name === ROUTE_NAMES.WORK_ENTITY &&
            from.params.entityIdentifier === to.params.entityIdentifier
        ) {
            logEntityLocatorNav("workEntityGuard.skip", {
                reason: "same_entity_identifier",
                entityIdentifier: to.params.entityIdentifier,
                to: to.fullPath,
                from: from.fullPath,
            });
            next();
            return;
        }

        const entityId = to.params.entityIdentifier;

        // Validate universal ID format
        if (!EntityResolver.isValidFormat(entityId)) {
            EntityErrorHandler.handleError({ response: { status: 400 } });
            useWorkspace().clearLastClickedEntity();
            next("/work");
            return;
        }

        let navigationConfirmed = false;

        try {
            // Ensure app is initialized
            const userStore = useUserStore();
            await userStore.initialize();

            const appState = useAppState();

            // initializeWith kicks off restoreSpaceState without awaiting it.
            // Wait until workspace_filter (and related space state) is settled
            // before EntityNavigator runs ensureDivisionFilterForNavigation —
            // otherwise ensure no-ops on an empty filter or gets overwritten.
            await waitForRestoredWorkspaceFilter(appState);

            if (appState.skip_locate_animation) {
                logEntityLocatorNav("workEntityGuard.skip", {
                    reason: "skip_locate_animation",
                    entityIdentifier: entityId,
                });
                appState.skip_locate_animation = false;
                next();
                return;
            }

            // Resolve universal ID to entity data
            logEntityLocatorNav("workEntityGuard.resolve.start", {
                entityIdentifier: entityId,
                to: to.fullPath,
                from: from.fullPath,
                fromModule: from.meta?.module,
            });
            const entityData = await EntityResolver.resolveEntity(entityId);

            const navEntry =
                typeof performance !== "undefined"
                    ? performance.getEntriesByType?.("navigation")?.[0]
                    : undefined;
            const cameFromNonWorkModule =
                from.meta?.module != null && from.meta.module !== "work";

            if (navEntry?.type === "reload") {
                logEntityLocatorNav("workEntityGuard.reload_detected", {
                    entityIdentifier: entityId,
                });
                appState.suppress_entity_locator_after_document_reload = true;
            }

            // Confirm navigation first so the Work panel can mount before locate animation.
            next();
            navigationConfirmed = true;

            await waitForWorkPanelPaint(cameFromNonWorkModule);

            try {
                logEntityLocatorNav("workEntityGuard.entityNavigator.start", {
                    entityType: entityData?.entity_type,
                    entityId: entityData?.entity_id,
                });
                await EntityNavigator.navigateToEntity(entityData);
                logEntityLocatorNav("workEntityGuard.entityNavigator.done", {
                    entityType: entityData?.entity_type,
                    entityId: entityData?.entity_id,
                });
            } finally {
                appState.suppress_entity_locator_after_document_reload = false;
            }
        } catch (error) {
            console.log(error);

            useWorkspace().clearLastClickedEntity();

            if (!navigationConfirmed) {
                next("/work");
            }

            EntityErrorHandler.handleError(error);
        } 
    } finally {
        // RouteHelper sets entity_navigate_with_locator = true as a "this nav
        // wants the locate animation" intent flag before calling router.push().
        // It used to be cleared by a fixed 550ms timer started as soon as
        // router.push() resolved — which is as soon as next() above is called,
        // well before EntityNavigator.navigateToEntity() (board/space loading +
        // the actual highlight check) finishes. When a board's data wasn't
        // cached yet, loadBoards() alone could exceed 550ms, so the flag was
        // already false by the time shouldPlayEntityLocatorHighlight() ran,
        // silently skipping the animation. This guard now owns clearing it,
        // exactly once it's actually done with the navigation (any exit path).
        logEntityLocatorNav("workEntityGuard.entity_navigate_with_locator.cleared", {
            reason: "guard_finished",
        });
        useAppState().entity_navigate_with_locator = false;
    }
}
